import React, { useState, useEffect } from 'react';
import { format, subDays, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, differenceInCalendarDays } from 'date-fns';
import { supabase } from '../../lib/supabase';

const inputClass = 'bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-darker text-sm focus:outline-none focus:border-yellow duration-300';

const presets = [
     { label: 'Today', range: () => { const d = new Date(); return [d, d]; } },
     { label: 'This Week', range: () => { const d = new Date(); return [subDays(d, d.getDay()), d]; } },
     { label: 'This Month', range: () => { const d = new Date(); return [startOfMonth(d), endOfMonth(d)]; } },
     { label: 'This Quarter', range: () => { const d = new Date(); return [startOfQuarter(d), endOfQuarter(d)]; } },
     { label: 'This Year', range: () => { const d = new Date(); return [startOfYear(d), endOfYear(d)]; } },
     { label: 'All Time', range: () => [new Date('2020-01-01'), new Date('2099-12-31')] },
];

export default function SalesDashboard() {
     const [from, setFrom] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
     const [to, setTo] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
     const [activePreset, setActivePreset] = useState('This Month');
     const [loading, setLoading] = useState(true);
     const [bookings, setBookings] = useState([]);
     const [expenses, setExpenses] = useState([]);
     const [allBookings, setAllBookings] = useState([]);

     useEffect(() => {
          fetchData();
          // eslint-disable-next-line
     }, [from, to]);

     async function fetchData() {
          setLoading(true);
          const [bookingsRes, expensesRes, allBookingsRes] = await Promise.all([
               supabase
                    .from('bookings')
                    .select('*')
                    .in('status', ['confirmed', 'completed'])
                    .gte('start_date', from)
                    .lte('start_date', to),
               supabase
                    .from('expenses')
                    .select('*')
                    .gte('date', from)
                    .lte('date', to),
               supabase
                    .from('bookings')
                    .select('customer_id, customer_email')
                    .in('status', ['confirmed', 'completed']),
          ]);
          setBookings(bookingsRes.data || []);
          setExpenses(expensesRes.data || []);
          setAllBookings(allBookingsRes.data || []);
          setLoading(false);
     }

     function applyPreset(preset) {
          const [s, e] = preset.range();
          setFrom(format(s, 'yyyy-MM-dd'));
          setTo(format(e, 'yyyy-MM-dd'));
          setActivePreset(preset.label);
     }

     const totalRevenue = bookings.reduce((s, b) => s + (b.total_cents || 0), 0);
     const totalExpenses = expenses.reduce((s, e) => s + (e.amount_cents || 0), 0);
     const avgOrder = bookings.length ? Math.round(totalRevenue / bookings.length) : 0;
     const netProfit = totalRevenue - totalExpenses;

     // Revenue by Equipment
     const equipmentMap = {};
     bookings.forEach(b => {
          const key = b.equipment_description || 'Unknown';
          if (!equipmentMap[key]) equipmentMap[key] = { count: 0, revenue: 0 };
          equipmentMap[key].count++;
          equipmentMap[key].revenue += b.total_cents || 0;
     });
     const equipmentList = Object.entries(equipmentMap)
          .map(([name, data]) => ({ name, ...data }))
          .sort((a, b) => b.revenue - a.revenue);

     // Delivery vs Pickup
     const deliveryStats = { delivery: { count: 0, revenue: 0 }, pickup: { count: 0, revenue: 0 } };
     bookings.forEach(b => {
          const method = b.delivery_method === 'delivery' ? 'delivery' : 'pickup';
          deliveryStats[method].count++;
          deliveryStats[method].revenue += b.total_cents || 0;
     });

     // Top 5 Customers
     const customerMap = {};
     bookings.forEach(b => {
          const key = b.customer_email || b.customer_name || 'Unknown';
          if (!customerMap[key]) customerMap[key] = { name: b.customer_name || key, count: 0, total: 0 };
          customerMap[key].count++;
          customerMap[key].total += b.total_cents || 0;
     });
     const topCustomers = Object.values(customerMap)
          .sort((a, b) => b.total - a.total)
          .slice(0, 5);

     // Deposit Summary
     const depositSummary = { held: { count: 0, amount: 0 }, refunded: { count: 0, amount: 0 }, captured: { count: 0, amount: 0 } };
     bookings.forEach(b => {
          const status = b.deposit_status || 'held';
          if (depositSummary[status]) {
               depositSummary[status].count++;
               depositSummary[status].amount += b.deposit_cents || 0;
          }
     });

     // New vs Returning
     const periodCustomerIds = new Set(bookings.map(b => b.customer_id || b.customer_email).filter(Boolean));
     const allCustomerBookingCounts = {};
     allBookings.forEach(b => {
          const key = b.customer_id || b.customer_email;
          if (key) allCustomerBookingCounts[key] = (allCustomerBookingCounts[key] || 0) + 1;
     });
     let newCustomers = 0;
     let returningCustomers = 0;
     periodCustomerIds.forEach(id => {
          if ((allCustomerBookingCounts[id] || 0) >= 2) returningCustomers++;
          else newCustomers++;
     });

     // Average Rental Duration
     const durations = bookings
          .filter(b => b.start_date && b.end_date)
          .map(b => differenceInCalendarDays(new Date(b.end_date), new Date(b.start_date)));
     const avgDuration = durations.length ? (durations.reduce((s, d) => s + d, 0) / durations.length).toFixed(1) : 0;

     const fmt = (cents) => `$${(cents / 100).toLocaleString()}`;

     return (
          <div>
               <h1 className='text-darker text-2xl font-bold mb-6'>Sales Dashboard</h1>

               {/* Date Range Picker */}
               <div className='bg-white rounded-xl p-5 border border-gray-200 mb-6'>
                    <div className='flex flex-wrap gap-2 mb-4'>
                         {presets.map(p => (
                              <button
                                   key={p.label}
                                   onClick={() => applyPreset(p)}
                                   className={`px-3 py-1.5 rounded-lg text-sm font-medium duration-300 ${
                                        activePreset === p.label
                                             ? 'bg-yellow text-black'
                                             : 'bg-gray-50 text-darker/60 hover:text-darker'
                                   }`}
                              >
                                   {p.label}
                              </button>
                         ))}
                    </div>
                    <div className='flex flex-wrap items-center gap-3'>
                         <label className='text-darker/40 text-sm'>From</label>
                         <input
                              type='date'
                              value={from}
                              onChange={e => { setFrom(e.target.value); setActivePreset(''); }}
                              className={inputClass}
                         />
                         <label className='text-darker/40 text-sm'>To</label>
                         <input
                              type='date'
                              value={to}
                              onChange={e => { setTo(e.target.value); setActivePreset(''); }}
                              className={inputClass}
                         />
                    </div>
               </div>

               {loading ? (
                    <p className='text-darker/60'>Loading sales data...</p>
               ) : (
                    <>
                         {/* Stats Cards */}
                         <div className='grid grid-cols-1 tablet:grid-cols-5 gap-4 mb-6'>
                              <div className='bg-white rounded-xl p-5 border border-gray-200'>
                                   <p className='text-darker/40 text-sm'>Total Revenue</p>
                                   <p className='text-yellow text-2xl font-bold mt-1'>{fmt(totalRevenue)}</p>
                              </div>
                              <div className='bg-white rounded-xl p-5 border border-gray-200'>
                                   <p className='text-darker/40 text-sm'>Total Bookings</p>
                                   <p className='text-darker text-2xl font-bold mt-1'>{bookings.length}</p>
                              </div>
                              <div className='bg-white rounded-xl p-5 border border-gray-200'>
                                   <p className='text-darker/40 text-sm'>Avg Order Value</p>
                                   <p className='text-darker text-2xl font-bold mt-1'>{fmt(avgOrder)}</p>
                              </div>
                              <div className='bg-white rounded-xl p-5 border border-gray-200'>
                                   <p className='text-darker/40 text-sm'>Total Expenses</p>
                                   <p className='text-red-400 text-2xl font-bold mt-1'>{fmt(totalExpenses)}</p>
                              </div>
                              <div className='bg-white rounded-xl p-5 border border-gray-200'>
                                   <p className='text-darker/40 text-sm'>Net Profit</p>
                                   <p className={`text-2xl font-bold mt-1 ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        {fmt(netProfit)}
                                   </p>
                              </div>
                         </div>

                         <div className='grid grid-cols-1 tablet:grid-cols-2 gap-6 mb-6'>
                              {/* Revenue by Equipment */}
                              <div className='bg-white rounded-xl p-5 border border-gray-200'>
                                   <h2 className='text-darker text-lg font-semibold mb-4'>Revenue by Equipment</h2>
                                   {equipmentList.length === 0 ? (
                                        <p className='text-darker/40 text-sm'>No data</p>
                                   ) : (
                                        <div className='space-y-3'>
                                             {equipmentList.map(item => (
                                                  <div key={item.name} className='flex items-center justify-between'>
                                                       <div className='min-w-0 flex-1 mr-3'>
                                                            <p className='text-darker text-sm truncate'>{item.name}</p>
                                                            <p className='text-darker/40 text-xs'>{item.count} booking{item.count !== 1 ? 's' : ''}</p>
                                                       </div>
                                                       <p className='text-yellow text-sm font-semibold whitespace-nowrap'>{fmt(item.revenue)}</p>
                                                  </div>
                                             ))}
                                        </div>
                                   )}
                              </div>

                              {/* Delivery vs Pickup */}
                              <div className='bg-white rounded-xl p-5 border border-gray-200'>
                                   <h2 className='text-darker text-lg font-semibold mb-4'>Delivery vs Pickup</h2>
                                   <div className='space-y-4'>
                                        {['delivery', 'pickup'].map(method => (
                                             <div key={method} className='flex items-center justify-between'>
                                                  <div>
                                                       <p className='text-darker text-sm capitalize'>{method}</p>
                                                       <p className='text-darker/40 text-xs'>{deliveryStats[method].count} booking{deliveryStats[method].count !== 1 ? 's' : ''}</p>
                                                  </div>
                                                  <p className='text-yellow text-sm font-semibold'>{fmt(deliveryStats[method].revenue)}</p>
                                             </div>
                                        ))}
                                   </div>
                              </div>
                         </div>

                         <div className='grid grid-cols-1 tablet:grid-cols-2 gap-6 mb-6'>
                              {/* Top 5 Customers */}
                              <div className='bg-white rounded-xl p-5 border border-gray-200'>
                                   <h2 className='text-darker text-lg font-semibold mb-4'>Top 5 Customers</h2>
                                   {topCustomers.length === 0 ? (
                                        <p className='text-darker/40 text-sm'>No data</p>
                                   ) : (
                                        <div className='space-y-3'>
                                             {topCustomers.map((c, i) => (
                                                  <div key={i} className='flex items-center justify-between'>
                                                       <div className='min-w-0 flex-1 mr-3'>
                                                            <p className='text-darker text-sm truncate'>
                                                                 <span className='text-yellow font-semibold mr-2'>#{i + 1}</span>
                                                                 {c.name}
                                                            </p>
                                                            <p className='text-darker/40 text-xs'>{c.count} booking{c.count !== 1 ? 's' : ''}</p>
                                                       </div>
                                                       <p className='text-yellow text-sm font-semibold whitespace-nowrap'>{fmt(c.total)}</p>
                                                  </div>
                                             ))}
                                        </div>
                                   )}
                              </div>

                              {/* Deposit Summary */}
                              <div className='bg-white rounded-xl p-5 border border-gray-200'>
                                   <h2 className='text-darker text-lg font-semibold mb-4'>Deposit Summary</h2>
                                   <div className='space-y-4'>
                                        {['held', 'refunded', 'captured'].map(status => (
                                             <div key={status} className='flex items-center justify-between'>
                                                  <div>
                                                       <p className='text-darker text-sm capitalize'>{status}</p>
                                                       <p className='text-darker/40 text-xs'>{depositSummary[status].count} deposit{depositSummary[status].count !== 1 ? 's' : ''}</p>
                                                  </div>
                                                  <p className='text-yellow text-sm font-semibold'>{fmt(depositSummary[status].amount)}</p>
                                             </div>
                                        ))}
                                   </div>
                              </div>
                         </div>

                         <div className='grid grid-cols-1 tablet:grid-cols-2 gap-6'>
                              {/* New vs Returning */}
                              <div className='bg-white rounded-xl p-5 border border-gray-200'>
                                   <h2 className='text-darker text-lg font-semibold mb-4'>New vs Returning Customers</h2>
                                   <div className='flex gap-6'>
                                        <div>
                                             <p className='text-darker/40 text-sm'>New</p>
                                             <p className='text-darker text-2xl font-bold'>{newCustomers}</p>
                                        </div>
                                        <div>
                                             <p className='text-darker/40 text-sm'>Returning</p>
                                             <p className='text-yellow text-2xl font-bold'>{returningCustomers}</p>
                                        </div>
                                   </div>
                              </div>

                              {/* Average Rental Duration */}
                              <div className='bg-white rounded-xl p-5 border border-gray-200'>
                                   <h2 className='text-darker text-lg font-semibold mb-4'>Average Rental Duration</h2>
                                   <p className='text-darker text-3xl font-bold'>
                                        {avgDuration} <span className='text-lg text-darker/40 font-normal'>days</span>
                                   </p>
                              </div>
                         </div>
                    </>
               )}
          </div>
     );
}
